/**
 * The exam wrapper's pure logic (pedagogy spec §7.3.3): lost points by cause,
 * the pattern, the share lost before any calculation, and at most two
 * suggestions for what to practise next.
 *
 * Zod-free, so the result page (the live pattern) and the server (the
 * reflection endpoint) share one implementation. No chapter numbers, no
 * `EXAM_SLOTS`, no book references: chapters and topics come from the
 * session's own questions at read time.
 */

import { EXAM_DURATION_MINUTES, EXAM_TOTAL_POINTS } from "./exam.js";
import { FOCUSED_COUNT, practiceDeepLink, type PracticeLink } from "./practiceLink.js";

/**
 * The causes a student can give for lost points, in the order the result page
 * shows them: the before-calculation causes, then the ones after it, then
 * `unknown` ("Vet inte") last. Stored reflections keep this order.
 */
export const EXAM_CAUSES = [
  "recognise",
  "principle",
  "concept",
  "setup",
  "algebra",
  "units",
  "explain",
  "time",
  "unknown",
] as const;
export type ExamCause = (typeof EXAM_CAUSES)[number];

/**
 * Lost before any calculation: recognising the type, choosing the principle,
 * knowing the theory behind it, setting it up. Every other cause except
 * `unknown` is after it.
 */
export const BEFORE_CALCULATION_CAUSES: readonly ExamCause[] = [
  "recognise",
  "principle",
  "concept",
  "setup",
];

/** The pattern's bars. `recognise` and `principle` share one bar (brief S8). */
export const PATTERN_GROUPS = [
  "principle",
  "setup",
  "algebra",
  "units",
  "time",
  "unassigned",
] as const;
export type PatternGroup = (typeof PATTERN_GROUPS)[number];

/**
 * Each cause's bar. `concept` ("didn't know the theory") and `explain` (a
 * missing justification) join `setup`, so they flow into the open-problems
 * suggestion for the tagged topics. `unknown` is an honest "Vet inte": it
 * counts as a choice but belongs to no bar, no suggestion and neither side of
 * the before-calculation sentence.
 */
export const CAUSE_GROUP: Record<ExamCause, Exclude<PatternGroup, "unassigned"> | null> = {
  recognise: "principle",
  principle: "principle",
  concept: "setup",
  setup: "setup",
  algebra: "algebra",
  units: "units",
  explain: "setup",
  time: "time",
  unknown: null,
};

/** The causes whose share lands in `group`, in `EXAM_CAUSES` order. */
export function causesInGroup(group: PatternGroup): ExamCause[] {
  return EXAM_CAUSES.filter((cause) => CAUSE_GROUP[cause] === group);
}

/** Reserved key in `exam_sessions.self_scores_json`; question ids never start with `_`. */
export const REFLECTION_KEY = "_reflection";
export const PLAN_MAX_LENGTH = 300;
/** Seconds per plan step in a "Which principle?" set: 12 problems in about 10 min (brief S8). */
export const PLAN_STEP_SECONDS = 50;
export const WHICH_PRINCIPLE_COUNT = 12;
/** The timed pair runs a little faster than the student's own exam pace. */
export const TIMED_PAIR_PACE_FACTOR = 0.8;
/** A timed pair is two exam problems and the client prefers 3-pointers (spec §7.3.6). */
export const TIMED_PAIR_POINTS = 6;
/** A pattern group earns a suggestion from this many lost points on. */
export const SUGGESTION_MIN_POINTS = 0.5;
export const MAX_SUGGESTIONS = 2;
/** "Lägg till i dagens pass" (E7): the suggestions stay in Today's session this many days. */
export const TODAY_EXTRAS_DAYS = 7;

/** One problem on a graded paper, joined from the session's questions and results. */
export interface WrapperProblem {
  questionId: string;
  slot: number;
  score: number;
  maxScore: number;
  chapter: number;
  /** The question's primary topic. */
  topic: string;
}

/** Question id → the causes the student chose for it. */
export type CauseMap = Readonly<Record<string, readonly ExamCause[]>>;

export interface GroupPoints {
  group: PatternGroup;
  points: number;
}

export type Suggestion =
  | { kind: "which-principle"; chapters: number[]; count: number; minutes: number }
  | { kind: "timed-pair"; chapters: number[]; perPointMinutes: number; minutes: number }
  | { kind: "setup"; topics: string[] }
  | { kind: "accuracy"; topics: string[] };

/** The sitting the pace comes from: `(deadlineAt − startedAt) / 60000` and Σ maxScore. */
export interface Sitting {
  minutes: number;
  paperPoints: number;
}

/** The default five-hour sitting of the real paper: the pace when the student has none of their own. */
export const DEFAULT_SITTING: Sitting = {
  minutes: EXAM_DURATION_MINUTES,
  paperPoints: EXAM_TOTAL_POINTS,
};

/**
 * Minutes per exam point of the timed pair: `TIMED_PAIR_PACE_FACTOR` × the sitting's own pace
 * (spec §13 Q16; 13.33 for the default sitting, so 80 minutes for six points). 0 for a paper
 * without points.
 */
export function timedPairPerPoint(sitting: Sitting): number {
  return sitting.paperPoints > 0
    ? (TIMED_PAIR_PACE_FACTOR * sitting.minutes) / sitting.paperPoints
    : 0;
}

/** Float noise below this counts as equal (scores are multiples of 0.25 or 0.5). */
const EPSILON = 1e-9;

export function isExamCause(value: unknown): value is ExamCause {
  return typeof value === "string" && (EXAM_CAUSES as readonly string[]).includes(value);
}

export function lostPoints(problem: WrapperProblem): number {
  const lost = problem.maxScore - problem.score;
  return lost > EPSILON ? lost : 0;
}

/** The problem's distinct, known causes in `EXAM_CAUSES` order; unrecognised ids are dropped. */
export function causesOf(causes: CauseMap, questionId: string): ExamCause[] {
  const chosen: readonly unknown[] = causes[questionId] ?? [];
  return EXAM_CAUSES.filter((cause) => chosen.includes(cause));
}

/**
 * Lost points per cause. A problem's loss is split equally over its distinct
 * causes; causes on a full-mark problem are ignored. `unknown` takes its share
 * like any other cause, so "algebra + Vet inte" puts half the loss on algebra.
 * Sums are unrounded; causes with no points are left out.
 */
export function lostByCause(
  problems: readonly WrapperProblem[],
  causes: CauseMap,
): Map<ExamCause, number> {
  const totals = new Map<ExamCause, number>();
  for (const problem of problems) {
    const lost = lostPoints(problem);
    if (lost === 0) continue;
    const chosen = causesOf(causes, problem.questionId);
    for (const cause of chosen) totals.set(cause, (totals.get(cause) ?? 0) + lost / chosen.length);
  }
  return totals;
}

/**
 * Lost points per pattern group, from `lostByCause`. A problem with a loss and
 * no cause counts as `unassigned`; the share of `unknown` ("Vet inte") goes to
 * no group, and a problem with only `unknown` is not `unassigned` either (the
 * student did answer). Sums are unrounded. Sorted by points, descending, then
 * in `PATTERN_GROUPS` order; empty groups are left out.
 */
export function lostByGroup(problems: readonly WrapperProblem[], causes: CauseMap): GroupPoints[] {
  const totals = new Map<PatternGroup, number>();
  const add = (group: PatternGroup, points: number) =>
    totals.set(group, (totals.get(group) ?? 0) + points);
  for (const problem of problems) {
    const lost = lostPoints(problem);
    if (lost > 0 && causesOf(causes, problem.questionId).length === 0) add("unassigned", lost);
  }
  for (const [cause, points] of lostByCause(problems, causes)) {
    const group = CAUSE_GROUP[cause];
    if (group) add(group, points);
  }
  return PATTERN_GROUPS.filter((group) => (totals.get(group) ?? 0) > EPSILON)
    .map((group) => ({ group, points: totals.get(group)! }))
    .sort((a, b) => {
      if (Math.abs(a.points - b.points) > EPSILON) return b.points - a.points;
      return PATTERN_GROUPS.indexOf(a.group) - PATTERN_GROUPS.indexOf(b.group);
    });
}

/**
 * Points lost before any calculation (`BEFORE_CALCULATION_CAUSES`: recognising
 * the type, choosing the principle, knowing the theory, setting it up) out of
 * the points that have a known cause. Counted per cause, not per bar, because
 * the set-up bar also holds `explain`, which comes after the calculation.
 * Unassigned points and the share of `unknown` are never in the denominator,
 * so the sentence cannot claim "0 of 6.5".
 */
export function beforeCalculation(
  problems: readonly WrapperProblem[],
  causes: CauseMap,
): {
  points: number;
  assigned: number;
} {
  let points = 0;
  let assigned = 0;
  for (const [cause, value] of lostByCause(problems, causes)) {
    if (CAUSE_GROUP[cause] === null) continue;
    assigned += value;
    if (BEFORE_CALCULATION_CAUSES.includes(cause)) points += value;
  }
  return { points, assigned };
}

/**
 * True when every lost point has a cause and that cause is always "Vet inte":
 * `lostByGroup` has nothing to show, yet the student did lose points. Shared
 * by `PatternCard` and `SuggestedNextCard`, so both say "no pattern"/"no
 * suggestion yet" instead of asking the student to choose causes they
 * already chose.
 */
export function onlyUnknownCauses(problems: readonly WrapperProblem[], causes: CauseMap): boolean {
  return lostByGroup(problems, causes).length === 0 && problems.some((p) => lostPoints(p) > 0);
}

/** `max(10, 5 · round(points · perPointMinutes / 5))` minutes. */
export function timedPairMinutes(points: number, perPointMinutes: number): number {
  return Math.max(10, 5 * Math.round((points * perPointMinutes) / 5));
}

function distinct<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function tagged(
  problems: readonly WrapperProblem[],
  causes: CauseMap,
  wanted: readonly ExamCause[],
): WrapperProblem[] {
  return problems
    .filter((problem) => lostPoints(problem) > 0)
    .filter((problem) => causesOf(causes, problem.questionId).some((c) => wanted.includes(c)))
    .sort((a, b) => a.slot - b.slot);
}

/**
 * What to practise next, driven by the causes (spec §7.3.3). Groups are ranked
 * by lost points; `unassigned` and `unknown` never count; a group qualifies from
 * `SUGGESTION_MIN_POINTS`. `principle` → a "Which principle?" set over the
 * tagged problems' chapters (every chapter on the paper when that is a single
 * chapter, so the set interleaves); `time` → a timed pair paced at
 * `TIMED_PAIR_PACE_FACTOR` × the sitting's minutes per point; `setup` (with
 * `concept` and `explain`) → open problems in the tagged problems' topics;
 * `units` and `algebra` share one `accuracy` suggestion. The first
 * `MAX_SUGGESTIONS` distinct kinds are returned.
 * Topic ids are returned as stored; the caller drops retired ones
 * (`dropInactiveTopics`).
 */
export function suggestNext(
  problems: readonly WrapperProblem[],
  causes: CauseMap,
  sitting: Sitting,
): Suggestion[] {
  const paperChapters = distinct(problems.map((p) => p.chapter)).sort((a, b) => a - b);
  const suggestions: Suggestion[] = [];
  for (const { group, points } of lostByGroup(problems, causes)) {
    if (suggestions.length >= MAX_SUGGESTIONS) break;
    if (group === "unassigned" || points < SUGGESTION_MIN_POINTS - EPSILON) continue;
    const kind: Suggestion["kind"] =
      group === "principle"
        ? "which-principle"
        : group === "time"
          ? "timed-pair"
          : group === "setup"
            ? "setup"
            : "accuracy";
    if (suggestions.some((s) => s.kind === kind)) continue;

    if (kind === "which-principle") {
      const own = distinct(
        tagged(problems, causes, causesInGroup("principle")).map((p) => p.chapter),
      ).sort((a, b) => a - b);
      suggestions.push({
        kind,
        chapters: own.length === 1 ? paperChapters : own,
        count: WHICH_PRINCIPLE_COUNT,
        minutes: Math.ceil((WHICH_PRINCIPLE_COUNT * PLAN_STEP_SECONDS) / 60),
      });
    } else if (kind === "timed-pair") {
      const perPointMinutes = timedPairPerPoint(sitting);
      suggestions.push({
        kind,
        chapters: paperChapters,
        perPointMinutes,
        minutes: timedPairMinutes(TIMED_PAIR_POINTS, perPointMinutes),
      });
    } else if (kind === "setup") {
      suggestions.push({
        kind,
        topics: distinct(tagged(problems, causes, causesInGroup("setup")).map((p) => p.topic)),
      });
    } else {
      suggestions.push({
        kind,
        topics: distinct(tagged(problems, causes, ["units", "algebra"]).map((p) => p.topic)),
      });
    }
  }
  return suggestions;
}

/**
 * Removes topic ids that are no longer active (Y&F retires some) and any
 * `setup`/`accuracy` suggestion left without a topic.
 */
export function dropInactiveTopics(
  suggestions: readonly Suggestion[],
  activeTopicIds: ReadonlySet<string>,
): Suggestion[] {
  const kept: Suggestion[] = [];
  for (const suggestion of suggestions) {
    if (suggestion.kind === "setup" || suggestion.kind === "accuracy") {
      const topics = suggestion.topics.filter((id) => activeTopicIds.has(id));
      if (topics.length > 0) kept.push({ kind: suggestion.kind, topics });
    } else {
      kept.push(suggestion);
    }
  }
  return kept;
}

/**
 * The practice link a suggestion's "Starta" follows (spec §4.3.9, step 2.8c): a
 * `which-principle` or `timed-pair` set that starts itself, or a short batch on
 * the suggestion's topics. `examSessionId` rides along as the set's provenance
 * (`origin.examSessionId`, stored for analysis and never trusted). The result
 * page and Today's extras both build their links here.
 */
export function suggestionLink(suggestion: Suggestion, examSessionId?: string): PracticeLink {
  switch (suggestion.kind) {
    case "which-principle":
      return {
        set: "which-principle",
        chapters: suggestion.chapters,
        count: suggestion.count,
        ...(examSessionId ? { from: examSessionId } : {}),
      };
    case "timed-pair":
      return {
        set: "timed-pair",
        chapters: suggestion.chapters,
        perPoint: suggestion.perPointMinutes,
        ...(examSessionId ? { from: examSessionId } : {}),
      };
    case "setup":
      return { topics: suggestion.topics, types: ["open"], count: FOCUSED_COUNT, primary: true };
    case "accuracy":
      return { topics: suggestion.topics, types: ["numeric"], count: FOCUSED_COUNT, primary: true };
  }
}

/** `practiceDeepLink(suggestionLink(…))`. */
export function suggestionHref(suggestion: Suggestion, examSessionId?: string): string {
  return practiceDeepLink(suggestionLink(suggestion, examSessionId));
}
